package dev.wyns.ventrova.ui.theme

import androidx.compose.material3.Typography
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.em
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.bricolage_grotesque_bold
import dev.wyns.ventrova.resources.bricolage_grotesque_extrabold
import dev.wyns.ventrova.resources.geist_bold
import dev.wyns.ventrova.resources.geist_medium
import dev.wyns.ventrova.resources.geist_regular
import dev.wyns.ventrova.resources.geist_semibold
import org.jetbrains.compose.resources.Font

/** Bricolage Grotesque, used for titles and place names on every platform. */
@Composable
fun displayFontFamily(): FontFamily = FontFamily(
    Font(Res.font.bricolage_grotesque_bold, FontWeight.Bold),
    Font(Res.font.bricolage_grotesque_extrabold, FontWeight.ExtraBold),
)

/** Geist, the body font of the web design (the browser canvas has no system fonts). */
@Composable
fun geistFontFamily(): FontFamily = FontFamily(
    Font(Res.font.geist_regular, FontWeight.Normal),
    Font(Res.font.geist_medium, FontWeight.Medium),
    Font(Res.font.geist_semibold, FontWeight.SemiBold),
    Font(Res.font.geist_bold, FontWeight.Bold),
)

/** Fonts of the current platform's design. */
data class VentrovaFonts(
    val display: FontFamily,
    /** System font on Android (Roboto) and iOS (SF Pro), Geist on web. */
    val body: FontFamily,
)

val LocalVentrovaFonts = staticCompositionLocalOf { VentrovaFonts(FontFamily.Default, FontFamily.Default) }

@Composable
fun ProvideVentrovaFonts(body: FontFamily, content: @Composable () -> Unit) {
    CompositionLocalProvider(
        LocalVentrovaFonts provides VentrovaFonts(display = displayFontFamily(), body = body),
        content = content,
    )
}

/** Title style in the display font, with the design's tight tracking. */
@Composable
fun displayStyle(size: Int, weight: FontWeight = FontWeight.Bold, lineHeight: Float = 1.1f) = TextStyle(
    fontFamily = LocalVentrovaFonts.current.display,
    fontWeight = weight,
    fontSize = size.sp,
    lineHeight = (size * lineHeight).sp,
    letterSpacing = if (size >= 30) (-0.03).em else (-0.02).em,
)

/** Material typography in [family]; the browser canvas has no system font to fall back on. */
fun Typography.withFontFamily(family: FontFamily): Typography = Typography(
    displayLarge = displayLarge.copy(fontFamily = family),
    displayMedium = displayMedium.copy(fontFamily = family),
    displaySmall = displaySmall.copy(fontFamily = family),
    headlineLarge = headlineLarge.copy(fontFamily = family),
    headlineMedium = headlineMedium.copy(fontFamily = family),
    headlineSmall = headlineSmall.copy(fontFamily = family),
    titleLarge = titleLarge.copy(fontFamily = family),
    titleMedium = titleMedium.copy(fontFamily = family),
    titleSmall = titleSmall.copy(fontFamily = family),
    bodyLarge = bodyLarge.copy(fontFamily = family),
    bodyMedium = bodyMedium.copy(fontFamily = family),
    bodySmall = bodySmall.copy(fontFamily = family),
    labelLarge = labelLarge.copy(fontFamily = family),
    labelMedium = labelMedium.copy(fontFamily = family),
    labelSmall = labelSmall.copy(fontFamily = family),
)
